import getSharePointFileLocation from '../../utils/getSharePointFileLocation';
import { ControlIcons } from 'owa-control-icons';
import { autobind } from '@uifabric/utilities/lib/autobind';
import { IconButton } from 'office-ui-fabric-react/lib/components/Button';
import { TooltipDelay, TooltipHost } from 'office-ui-fabric-react/lib/components/Tooltip';
import { DirectionalHint } from 'office-ui-fabric-react/lib/common/DirectionalHint';
import AttachmentFileInformation from 'owa-group-files-hub-store/lib/Schema/AttachmentFileInformation';
import BaseFileInformation, {
    FileType,
} from 'owa-group-files-hub-store/lib/Schema/BaseFileInformation';
import CloudAttachmentFileInformation, {
    ProviderType,
} from 'owa-group-files-hub-store/lib/Schema/CloudAttachmentFileInformation';
import SharepointFileInformation from 'owa-group-files-hub-store/lib/Schema/SharePointFileInformation.ts';

import { logUsage } from 'owa-analytics';
import { getIconForFile } from 'owa-file-icon';
import { getCurrentGroupSmtpAddress } from 'owa-groups-shared-store';
import { getGroupDetails } from 'owa-groups-shared-store';
import {
    clickRecentFileCardAction,
    downloadRecentFileAction,
    getFirstEmailAddress,
} from 'owa-group-files-hub-actions';
import { LivePersonaCardWrapper } from 'owa-groups-shared-components';
import KeyboardCharCodes from 'owa-hotkeys/lib/utils/keyboardCharCodes';
import strings from 'owa-strings/owa-groups-strings';
import { observer } from 'mobx-react';
import * as React from 'react';

const styles = require('./RecentFileCard.scss');

export interface Props {
    fileInformation: BaseFileInformation;
    cardIndex: number;
    totalNumberOfCards: number;
    groupSiteUrlAvailable: boolean;
}

@observer
export default class RecentFileCard extends React.Component<Props, {}> {
    constructor(props: Props) {
        super(props);
    }

    @autobind
    private performDefaultOnClickAction(fileInformation: BaseFileInformation): void {
        clickRecentFileCardAction(fileInformation, this.props.cardIndex);
    }

    @autobind
    private onDownloadClick(fileInformation: BaseFileInformation): void {
        logUsage('GroupFilesHubRecentFilesCardDownloadClick', {
            IndexOfCard: this.props.cardIndex.toString(),
        });

        downloadRecentFileAction(fileInformation);
    }

    @autobind
    private getFileInfoForSpecificFile() {
        let fileSubText = null;
        let fileInfo = null;
        let isAttachment = false;
        let downloadable = true;

        if (this.props.fileInformation) {
            switch (this.props.fileInformation.fileType) {
                case FileType.ClassicAttachment:
                    fileInfo = this.props.fileInformation as AttachmentFileInformation;
                    fileSubText = fileInfo.message.subject;
                    isAttachment = true;
                    break;
                case FileType.CloudAttachment:
                    fileInfo = this.props.fileInformation as CloudAttachmentFileInformation;
                    fileSubText = fileInfo.message.subject;
                    isAttachment = true;
                    downloadable =
                        (fileInfo.providerType as ProviderType) === ProviderType.OneDrivePro;
                    break;
                case FileType.SharePointFile:
                    fileInfo = this.props.fileInformation as SharepointFileInformation;
                    fileSubText = getSharePointFileLocation(
                        fileInfo.url,
                        getGroupDetails(getCurrentGroupSmtpAddress())
                    );
                    downloadable = this.props.groupSiteUrlAvailable;
                    break;
                default:
                    break;
            }
        }

        return { fileSubText, isAttachment, downloadable };
    }

    @autobind
    private renderHeader(): JSX.Element {
        const lastModifiedBy = this.props.fileInformation.lastModifiedBy;
        const modifiedByEmailAddress = getFirstEmailAddress(lastModifiedBy);

        return (
            <LivePersonaCardWrapper
                name={lastModifiedBy.DisplayName}
                emailAddress={modifiedByEmailAddress && modifiedByEmailAddress.EmailAddress}
                personaType={'User'}
                className={styles.personname}>
                {lastModifiedBy.DisplayName}
            </LivePersonaCardWrapper>
        );
    }

    @autobind
    private renderFilePreview() {
        const iconCSSclassName = getIconForFile(this.props.fileInformation.name, false);

        return (
            <div className={styles.fileThumbnailContainer}>
                <div className={iconCSSclassName.medium} />
            </div>
        );
    }

    private getAriaLabelDescribingFile(fileType: string, fileSubText: string): string {
        let ariaLabel;
        const cardIndex: number = this.props.cardIndex + 1;
        if (this.props.totalNumberOfCards > 1) {
            ariaLabel = strings.fileshubRecentFileCardAriaDescription;
            ariaLabel = ariaLabel
                .replace('{0}', cardIndex)
                .replace('{1}', this.props.totalNumberOfCards)
                .replace('{2}', this.props.fileInformation.name)
                .replace('{3}', fileType)
                .replace('{4}', this.props.fileInformation.lastModifiedBy.DisplayName)
                .replace('{5}', fileSubText);
        } else {
            ariaLabel = strings.fileshubRecentFileCardAriaDescriptionWhenTotalFileCountIsOne;
            ariaLabel = ariaLabel
                .replace('{0}', cardIndex)
                .replace('{1}', this.props.fileInformation.name)
                .replace('{2}', fileType)
                .replace('{3}', this.props.fileInformation.lastModifiedBy.DisplayName)
                .replace('{4}', fileSubText);
        }
        return ariaLabel;
    }

    private onCardKeyDown = (evt: React.KeyboardEvent<HTMLDivElement>) => {
        switch (evt.keyCode) {
            case KeyboardCharCodes.Enter:
            case KeyboardCharCodes.Space:
                evt.stopPropagation();
                this.performDefaultOnClickAction(this.props.fileInformation);
                break;
        }
    };

    private onDownloadButtonKeyDown = (evt: React.KeyboardEvent<HTMLDivElement>) => {
        switch (evt.keyCode) {
            case KeyboardCharCodes.Enter:
            case KeyboardCharCodes.Space:
                // The reason we are not calling the function to download file here is key press calls both OnKeyDown and OnClick event.
                // so we just stop the evt to propogate for now and not do anything as OnClick will download the file.
                evt.stopPropagation();
                break;
        }
    };

    render(): JSX.Element {
        const { fileSubText, isAttachment, downloadable } = this.getFileInfoForSpecificFile();
        const fileTypeString = isAttachment
            ? strings.fileshubRecentFileCardIsAttachment
            : strings.fileshubRecentFileCardIsSharePoint;

        return (
            <div
                className={styles.container}
                aria-label={this.getAriaLabelDescribingFile(fileTypeString, fileSubText)}
                tabIndex={0}
                onClick={() => this.performDefaultOnClickAction(this.props.fileInformation)}
                onKeyDown={this.onCardKeyDown}>
                <div className={styles.personaContainer}>
                    {this.renderHeader()}
                    <div className={styles.fileTypeContainer} title={fileTypeString}>
                        {fileTypeString}
                    </div>
                </div>

                <div className={styles.filePreviewContainer}>{this.renderFilePreview()}</div>

                <div className={styles.fileInfoAndActionContainer}>
                    <div className={styles.fileDetailsContainer}>
                        <div className={styles.fileNameContainer}>
                            <div
                                className={styles.fileName}
                                title={this.props.fileInformation.name}>
                                {this.props.fileInformation.name}
                            </div>
                        </div>
                        <div className={styles.fileLinkContainer}>
                            <div className={styles.fileSubText} title={fileSubText}>
                                {fileSubText}
                            </div>
                        </div>
                    </div>

                    <div className={styles.fileActionsContainer}>
                        <TooltipHost
                            content={
                                downloadable
                                    ? strings.fileshubRecentFileCardDownloadTooltip
                                    : strings.fileshubRecentFileCardDownloadNotAvailableTooltip
                            }
                            delay={TooltipDelay.long}
                            directionalHint={DirectionalHint.topCenter}>
                            <IconButton
                                iconProps={{ iconName: ControlIcons.Download }}
                                name={strings.fileshubDownloadButtonTitle}
                                aria-label={strings.fileshubDownloadButtonTitle}
                                disabled={!downloadable}
                                onClick={e => {
                                    e.stopPropagation();
                                    this.onDownloadClick(this.props.fileInformation);
                                }}
                                onKeyDown={this.onDownloadButtonKeyDown}
                            />
                        </TooltipHost>
                    </div>
                </div>
            </div>
        );
    }
}
